// API Models

import {
  SENSORS_HCPCS_GROUP,
  SETS_RESERVOIRS_HCPCS_GROUP,
  TESTING_SUPPLIES_HCPCS_GROUP,
  REORDER_CONCLUSION_SHIPMENT_TYPE_IDS,
} from '../constants';
import { Address, ProductGroupHcpcs, YesOrNo } from '.';
import * as moment from 'moment';

export enum ChargeTypeLong {
  Insurance = 'Insurance',
  NoCharge = 'No Charge',
  CashSale = 'Cash Sale',
}

export interface ApiReorderDetailsItemResponse {
  itemId: number;
  uiId: string;
  shipItem: YesOrNo;
  itemDescription: string;
  category: string;
  quantity?: number;
  total?: number;
  chargeType: ChargeTypeLong;
  shipmentTypeId?: number;
  dupItem: string;
  hcpcsCode?: string;
  // Validation related fields
  validShippingQuantity?: number;
  docCoverage: 'PWO' | 'RX' | 'NONE';
  isDocRequired: YesOrNo;
  itemQuantityValidationMessage?: string;
  quantityIsValid: YesOrNo;
}

export interface ApiReorderDetailsResponse {
  referenceNo: number;
  items: ApiReorderDetailsItemResponse[];
  shipmentDate?: string;
  shipmentTypeId?: number;
}

// Front End Models

export interface ReorderDetailsItem {
  id: number;
  uiId: string;
  description: string;
  category: string;
  shipmentTypeId?: number;
  hcpcsCode?: string;
  chargeType: ChargeTypeLong;
  // Fields from other APIs
  daysOnHandRequired?: boolean;
  // Computed Fields
  isDuplicate: boolean;
  isCashSale: boolean;
  isChargeable: boolean | null;
  isNoCharge: boolean;
  hcpcsGroup: string | null;
  isMissingDoc: boolean;
  quantity?: number;
  minValidShippingQuantity: number | null;
  maxValidShippingQuantity: number | null;
  submitInvalidQuantity: boolean;
  isMedicareOrMedicareRepl: boolean; // Added to easily compute validation messages later
  quantityValidationIssue: string | null;
}

export interface ReorderDetails {
  id: number;
  items: ReorderDetailsItem[];
  targetedShipDate?: string;
  shipmentTypeId?: number;
  shippingAddress?: Address;
  // Computed fields
  isNonBillableShipmentType: boolean;
  isReorderConclusionShipment: boolean;
}

// Mappers

export const mapReorderDetailsItem = (
  roi: ApiReorderDetailsItemResponse,
  isMedicareOrMedicareRepl: boolean,
  isReorderConclusionShipment: boolean,
  hcpcsGroups: ProductGroupHcpcs[]
): ReorderDetailsItem => {
  const itemQuantity = roi.quantity || 0;
  const hcpcsGroup = roi.hcpcsCode
    ? hcpcsGroups.find((hg) => hg.groupHcpcsCodes.includes(roi.hcpcsCode!))?.productGroupName || null
    : null;

  let quantity = itemQuantity;
  let minValidShippingQuantity = null;
  let maxValidShippingQuantity = null;
  let submitInvalidQuantity = false;
  let quantityValidationIssue = null;

  const isNoCharge = roi.chargeType === ChargeTypeLong.NoCharge;
  const isMissingDoc = roi.isDocRequired === YesOrNo.Y && roi.docCoverage === 'NONE';
  const pwoValidShippingQuantity = roi.validShippingQuantity;

  const shouldValidateQuantity = isReorderConclusionShipment && !isNoCharge && !isMissingDoc;

  if (shouldValidateQuantity) {
    if (!pwoValidShippingQuantity && roi.quantityIsValid === YesOrNo.N) {
      quantityValidationIssue = `Missing valid shipping quantity. ${
        roi.itemQuantityValidationMessage || 'No validation message available.'
      }`;
    } else if (hcpcsGroup === SETS_RESERVOIRS_HCPCS_GROUP) {
      if (isMedicareOrMedicareRepl) {
        quantity = pwoValidShippingQuantity || itemQuantity;
        minValidShippingQuantity = pwoValidShippingQuantity || itemQuantity;
        maxValidShippingQuantity = pwoValidShippingQuantity || itemQuantity;
      } else {
        quantity = Math.min(itemQuantity, pwoValidShippingQuantity || itemQuantity);
        minValidShippingQuantity = null;
        maxValidShippingQuantity = pwoValidShippingQuantity || itemQuantity;
      }
    } else if (hcpcsGroup === SENSORS_HCPCS_GROUP) {
      quantity = Math.min(itemQuantity, pwoValidShippingQuantity || itemQuantity);
      minValidShippingQuantity = null;
      maxValidShippingQuantity = pwoValidShippingQuantity || itemQuantity;
    } else if (hcpcsGroup === TESTING_SUPPLIES_HCPCS_GROUP) {
      quantity = itemQuantity;
      minValidShippingQuantity = itemQuantity;
      maxValidShippingQuantity = itemQuantity;
      submitInvalidQuantity = true;
    }
  }

  return {
    id: roi.itemId,
    uiId: roi.uiId,
    description: roi.itemDescription,
    category: roi.category,
    shipmentTypeId: roi.shipmentTypeId,
    hcpcsCode: roi.hcpcsCode,
    chargeType: roi.chargeType,
    // Computed Fields
    isMissingDoc,
    isDuplicate: roi.dupItem === YesOrNo.Y,
    isCashSale: roi.chargeType === ChargeTypeLong.CashSale,
    isChargeable:
      roi.chargeType === ChargeTypeLong.Insurance ? true : roi.chargeType === ChargeTypeLong.NoCharge ? false : null, // Following ipims
    isNoCharge,
    hcpcsGroup,
    quantity,
    minValidShippingQuantity,
    maxValidShippingQuantity,
    submitInvalidQuantity,
    isMedicareOrMedicareRepl,
    quantityValidationIssue,
  };
};

export const mapReorderDetails = (
  ro: ApiReorderDetailsResponse,
  isMedicareOrMedicareRepl: boolean,
  hcpcsGroups: ProductGroupHcpcs[]
): ReorderDetails => {
  const isReorderConclusionShipment = ro.shipmentTypeId
    ? REORDER_CONCLUSION_SHIPMENT_TYPE_IDS.includes(ro.shipmentTypeId)
    : false;

  const now = moment();

  return {
    id: ro.referenceNo,
    items: (ro.items || [])
      .filter((roi) => roi.shipItem === YesOrNo.Y && roi.quantity)
      .map((roi) => mapReorderDetailsItem(roi, isMedicareOrMedicareRepl, isReorderConclusionShipment, hcpcsGroups)),
    targetedShipDate:
      ro.shipmentDate && (now.isAfter(moment(ro.shipmentDate), 'day') ? now.format('YYYY-MM-DD') : ro.shipmentDate),
    shipmentTypeId: ro.shipmentTypeId,
    isNonBillableShipmentType: !ro.shipmentTypeId || !REORDER_CONCLUSION_SHIPMENT_TYPE_IDS.includes(ro.shipmentTypeId),
    isReorderConclusionShipment,
  };
};
